import { Box, Button, Divider, Stack, styled, Typography } from '@mui/material';

import {
  OrganizationsService,
  formatDateTo,
  formatMonetaryAmount,
  getPlanEstimations,
  isDarkMode,
  useApiGetter,
} from '@localstack/services';
import { PriceWithDiscount, ProgressButton } from '@localstack/ui';

import { EmulatorType, OrderContext, OrganizationRestrictionReason, Plan } from '@localstack/types';
import { AWS_BASE_PLAN_KEY_FEATURES, AWS_ULTIMATE_PLAN_KEY_FEATURES } from '@localstack/constants';

import { Info } from '@mui/icons-material';

import { pricingRoute } from '~/util/pricing';

import { useSubscribeToFreemiumPlan } from '~/hooks/useSubscribeToFreemiumPlan';

import { FeatureList, GridComponent, PLAN_INFO_WIDTH } from '../utils';

const UpgradePromptContainer = styled(Stack)(({ theme }) => ({
  backgroundColor: isDarkMode(theme) ? theme.palette.background.paper : theme.palette.grey[100],
  color: isDarkMode(theme) ? theme.palette.grey[300] : theme.palette.grey[600],
  padding: '12px 16px',
}));

/** Row to be shown if user has no subscriptions active, i.e. whatever we default to */
export const NoSubscriptionsPrompt = (): JSX.Element => {
  const {
    memberRemovalModal,
    triggerSubscriptionFlow,
    isCreatingSubscription,
    isCheckingEligibility,
    canSubscribeToFreemiumPlan,
  } = useSubscribeToFreemiumPlan();

  const { data: orgRestrictions } = useApiGetter(OrganizationsService, 'listOrganizationRestrictions', [], {
    defaultValue: [],
  });
  const isFreemiumHobbyCommercialMisuse =
    orgRestrictions?.some((r) => r.reason === OrganizationRestrictionReason.FREEMIUM_HOBBY_COMMERCIAL_MISUSE) ?? false;

  return (
    <>
      <GridComponent>
        <Typography color="text.tertiary">No subscriptions found.</Typography>
      </GridComponent>
      <Divider sx={{ m: 0 }} />
      {memberRemovalModal}
      <UpgradePromptContainer direction={'row'} justifyContent="space-between" alignItems={'center'}>
        <Typography color="text.tertiary">
          {isFreemiumHobbyCommercialMisuse
            ? 'Upgrade for more features.'
            : 'Use our Hobby plan or upgrade for more features.'}
        </Typography>
        <Box flexDirection={'row'} display={'flex'} gap={'8px'}>
          {!isCheckingEligibility && canSubscribeToFreemiumPlan && !isFreemiumHobbyCommercialMisuse && (
            <ProgressButton
              onClick={() => triggerSubscriptionFlow()}
              size="small"
              variant="outlined"
              disableElevation
              disabled={isCreatingSubscription}
              loading={isCreatingSubscription}
            >
              Continue Free
            </ProgressButton>
          )}
          <ProgressButton
            loading={isCheckingEligibility}
            href={pricingRoute()}
            size="small"
            color="primary"
            variant="contained"
            disableElevation
          >
            Compare plans
          </ProgressButton>
        </Box>
      </UpgradePromptContainer>
    </>
  );
};

const getPlanPriceText = (plan: Plan): { current: string; original?: string } => {
  const context: OrderContext = { seats: 1, org_id: '', customer_id: '' };
  const fullAmount = getPlanEstimations(plan.products, context, plan.discount, false);
  const discountedAmount = getPlanEstimations(plan.products, context, plan.discount, true);
  const fullMonthly = fullAmount / plan.interval_months;
  const discountedMonthly = discountedAmount / plan.interval_months;
  const formattedFull = formatMonetaryAmount(fullMonthly, plan.currency);
  const formattedDiscounted = formatMonetaryAmount(discountedMonthly, plan.currency);

  return discountedMonthly < fullMonthly
    ? { current: formattedDiscounted, original: formattedFull }
    : { current: formattedFull };
};

export const UpgradeToBase = ({ plan }: { plan: Plan }): JSX.Element => {
  const priceText = getPlanPriceText(plan);

  return (
    <>
      <Divider sx={{ m: 0 }} />
      <UpgradePromptContainer direction={'row'} gap="16px" alignItems={'center'}>
        <Stack gap="2px" width={PLAN_INFO_WIDTH} flexShrink={0}>
          <Typography color="text.primary" fontWeight={500}>
            Base
          </Typography>
          <PriceWithDiscount
            discountedPrice={priceText.current}
            price={priceText.original}
            discountedPriceProps={{ color: 'text.tertiary' }}
            priceProps={{ color: 'text.tertiary' }}
            suffix={<Typography color="text.tertiary">per seat/mo.</Typography>}
            gap={0.5}
          />
          <Button
            color="primary"
            variant="contained"
            disableElevation
            sx={{ width: 'fit-content', marginTop: '20px' }}
            href={pricingRoute([plan.emulator])}
          >
            Upgrade plan
          </Button>
        </Stack>
        <FeatureList features={AWS_BASE_PLAN_KEY_FEATURES} />
      </UpgradePromptContainer>
    </>
  );
};

export const UpgradeToUltimate = ({ plan }: { plan: Plan }): JSX.Element => {
  const priceText = getPlanPriceText(plan);

  return (
    <>
      <Divider sx={{ m: 0 }} />
      <UpgradePromptContainer direction={'row'} gap="16px" alignItems={'center'}>
        <Stack gap="2px" width={PLAN_INFO_WIDTH} flexShrink={0}>
          <Typography color="text.primary" fontWeight={500}>
            Ultimate
          </Typography>
          <PriceWithDiscount
            discountedPrice={priceText.current}
            price={priceText.original}
            discountedPriceProps={{ color: 'text.tertiary' }}
            priceProps={{ color: 'text.tertiary' }}
            suffix={<Typography color="text.tertiary">per seat/mo.</Typography>}
            gap={0.5}
          />
          <Button
            color="primary"
            variant="contained"
            disableElevation
            sx={{ width: 'fit-content', marginTop: '20px' }}
            href={pricingRoute([plan.emulator])}
          >
            Upgrade plan
          </Button>
        </Stack>
        <FeatureList features={AWS_ULTIMATE_PLAN_KEY_FEATURES} />
      </UpgradePromptContainer>
    </>
  );
};

const BasicPromptComponent = ({
  infoText,
  buttonText,
  emulator,
}: {
  infoText: React.ReactNode;
  buttonText: string;
  emulator: EmulatorType;
}): JSX.Element => (
  <>
    <Divider sx={{ m: 0 }} />
    <UpgradePromptContainer direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
      <Stack direction="row" gap={1}>
        <Info color="inherit" fontSize="small" />
        <Typography variant="body1" color="inherit">
          {infoText}
        </Typography>
      </Stack>
      <Button href={pricingRoute([emulator])} size="small" color="primary" variant="contained" disableElevation>
        {buttonText}
      </Button>
    </UpgradePromptContainer>
  </>
);

/** Prompt to be shown to user to upgrade if they are currently in a Trial */
export const UpgradeTrialPrompt = ({
  trialEndDate,
  emulator,
}: {
  trialEndDate: number;
  emulator: EmulatorType;
}): JSX.Element => (
  <BasicPromptComponent
    infoText={
      <>
        You&apos;ll need to upgrade to continue using it beyond <b>{formatDateTo(trialEndDate, 'MMM DD, YYYY')}</b>.
      </>
    }
    buttonText="Upgrade plan"
    emulator={emulator}
  />
);

/** Prompt to be shown to the user if all their subscriptions are in the cancelling state */
export const CancellingPlansPrompt = ({ emulator }: { emulator: EmulatorType }): JSX.Element => (
  <BasicPromptComponent
    infoText={'All plans are scheduled to cancel.'}
    buttonText="Add subscription"
    emulator={emulator}
  />
);

/** Prompt to be shown to the user if they have a legacy plan as those can't be converted to newer plans */
export const LegacyPlansPrompt = ({ emulator }: { emulator: EmulatorType }): JSX.Element => (
  <BasicPromptComponent
    infoText={'Add one of our latest plans for more services and more.'}
    buttonText="Add subscription"
    emulator={emulator}
  />
);
